import {
  createBrowserRouter,
  Navigate,
  type RouteObject,
  useLocation,
  useParams,
  useRouteError,
} from "react-router-dom";

import { CrashFallback } from "./components/ui/ErrorBoundary";
import { ProtectedRoute } from "./routes/ProtectedRoute";
import { RuleWizardPage } from "./routes/rules/RuleWizardPage";
import { AuditLogPage } from "./routes/audit/AuditLogPage";
import { ConfigurationWorkspace } from "./routes/config/ConfigurationWorkspace";
import { SetupWizard } from "./routes/setup/SetupWizard";
import { DashboardPage } from "./routes/dashboard/DashboardPage";
import { DeviceDetailPage } from "./routes/devices/DeviceDetailPage";
import { DevicesListPage } from "./routes/devices/DevicesListPage";
import { EnrollmentBatchDetailPage } from "./routes/enrollment/EnrollmentBatchDetailPage";
import { EnrollmentListPage } from "./routes/enrollment/EnrollmentListPage";
import { InsightsWorkspace } from "./routes/insights/InsightsWorkspace";
import { LoginPage } from "./routes/login/LoginPage";
import { PoliciesWorkspace } from "./routes/policies/PoliciesWorkspace";
import { PrivacyPage } from "./routes/privacy/PrivacyPage";
import { AppAdoptionReportPage } from "./routes/reports/AppAdoptionReportPage";
import { DeviceHealthReportPage } from "./routes/reports/DeviceHealthReportPage";
import { SettingsPage } from "./routes/settings/SettingsPage";
import { EmployeesListPage } from "./routes/employees/EmployeesListPage";
import { UserEditorPage } from "./routes/users/UserEditorPage";
import { UsersListPage } from "./routes/users/UsersListPage";

function ScheduleEditorRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/policies/schedules?id=${encodeURIComponent(id ?? "new")}`} replace />;
}

/**
 * Legacy pre-redesign URLs collapse onto their canonical workspace path via
 * a bare `<Navigate>`. That silently dropped `location.search` — deep-link
 * filters like `/rules?destinationGroup=…` landed on the unfiltered list
 * with no hint (see docs/admin/ux-audit.md P1-2). This forwards the
 * incoming query string onto the target, merging rather than clobbering
 * when the target already hard-codes its own query (e.g. `/catalog`).
 */
function RedirectWithSearch({ to }: { to: string }) {
  const location = useLocation();
  if (!location.search) return <Navigate to={to} replace />;

  const [targetPath, targetQuery] = to.split("?");
  const params = new URLSearchParams(targetQuery ?? "");
  for (const [key, value] of new URLSearchParams(location.search)) {
    if (!params.has(key)) params.set(key, value);
  }
  const query = params.toString();
  return <Navigate to={query ? `${targetPath}?${query}` : targetPath} replace />;
}

/**
 * `createBrowserRouter` renders an unstyled, English-only default error
 * screen for any route without its own `errorElement` — see the note on
 * `ErrorBoundary` (components/ui/ErrorBoundary.tsx) for why the app-level
 * class boundary alone can't reach these. Attached to every route below so
 * a render exception inside any page shows the same localized fallback.
 */
function RouteErrorElement() {
  const error = useRouteError();
  console.error("Unhandled route error", error);
  return <CrashFallback />;
}

const policiesRoutes = [
  { path: "/policies", element: <RedirectWithSearch to="/policies/rules" /> },
  { path: "/policies/hub", element: <RedirectWithSearch to="/policies/rules" /> },
  { path: "/policies/:tab", element: <PoliciesWorkspace /> },
  { path: "/policies/schedules/new", element: <Navigate to="/policies/schedules?id=new" replace /> },
  { path: "/policies/schedules/:id", element: <ScheduleEditorRedirect /> },
];

const routeConfig: RouteObject[] = [
  { path: "/", element: <Navigate to="/dashboard" replace /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/privacy", element: <PrivacyPage /> },
  {
    path: "/dashboard",
    element: (
      <ProtectedRoute>
        <DashboardPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/enrollment",
    element: (
      <ProtectedRoute>
        <EnrollmentListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/enrollment/batches/:id",
    element: (
      <ProtectedRoute>
        <EnrollmentBatchDetailPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/devices",
    element: (
      <ProtectedRoute>
        <DevicesListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/devices/:id",
    element: (
      <ProtectedRoute>
        <DeviceDetailPage />
      </ProtectedRoute>
    ),
  },
  { path: "/catalog", element: <RedirectWithSearch to="/policies/groups?kind=app" /> },
  { path: "/monitoring", element: <RedirectWithSearch to="/insights/violations" /> },
  {
    path: "/monitoring/devices/:id",
    element: (
      <ProtectedRoute>
        <DeviceDetailPage />
      </ProtectedRoute>
    ),
  },
  { path: "/govern", element: <RedirectWithSearch to="/policies/rules" /> },
  { path: "/domains", element: <RedirectWithSearch to="/policies/domains" /> },
  { path: "/network/url-pool", element: <RedirectWithSearch to="/policies/domains" /> },
  { path: "/violations", element: <RedirectWithSearch to="/insights/violations" /> },
  { path: "/rules", element: <RedirectWithSearch to="/policies/rules" /> },
  {
    path: "/rules/new",
    element: (
      <ProtectedRoute>
        <RuleWizardPage />
      </ProtectedRoute>
    ),
  },
  { path: "/network/url-groups", element: <RedirectWithSearch to="/policies/groups" /> },
  { path: "/network/device-groups", element: <RedirectWithSearch to="/policies/groups" /> },
  { path: "/network/group-policies", element: <RedirectWithSearch to="/policies/rules" /> },
  { path: "/network/events", element: <RedirectWithSearch to="/insights/violations" /> },
  {
    path: "/network/access-requests",
    element: <RedirectWithSearch to="/insights/access-requests" />,
  },
  ...policiesRoutes.map((r) => ({
    path: r.path,
    element: <ProtectedRoute>{r.element}</ProtectedRoute>,
  })),
  {
    path: "/users",
    element: (
      <ProtectedRoute>
        <UsersListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/users/new",
    element: (
      <ProtectedRoute>
        <UserEditorPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/users/:id",
    element: (
      <ProtectedRoute>
        <UserEditorPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/audit",
    element: (
      <ProtectedRoute>
        <AuditLogPage />
      </ProtectedRoute>
    ),
  },
  { path: "/reports", element: <RedirectWithSearch to="/insights/reports" /> },
  { path: "/reports/app-usage", element: <RedirectWithSearch to="/insights/reports" /> },
  {
    path: "/reports/device-health",
    element: (
      <ProtectedRoute>
        <DeviceHealthReportPage />
      </ProtectedRoute>
    ),
  },
  { path: "/reports/app-inventory", element: <RedirectWithSearch to="/insights/reports" /> },
  { path: "/reports/app-usage/:appId", element: <RedirectWithSearch to="/insights/reports" /> },
  {
    path: "/reports/app-adoption",
    element: (
      <ProtectedRoute>
        <AppAdoptionReportPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/employees",
    element: (
      <ProtectedRoute>
        <EmployeesListPage />
      </ProtectedRoute>
    ),
  },
  {
    path: "/settings",
    element: (
      <ProtectedRoute>
        <SettingsPage />
      </ProtectedRoute>
    ),
  },
  { path: "/config", element: <RedirectWithSearch to="/config/users" /> },
  {
    path: "/config/:tab",
    element: (
      <ProtectedRoute>
        <ConfigurationWorkspace />
      </ProtectedRoute>
    ),
  },
  { path: "/insights", element: <RedirectWithSearch to="/insights/violations" /> },
  { path: "/insights/overview", element: <RedirectWithSearch to="/insights/violations" /> },
  { path: "/insights/app-usage", element: <RedirectWithSearch to="/insights/reports" /> },
  {
    path: "/insights/:tab",
    element: (
      <ProtectedRoute>
        <InsightsWorkspace />
      </ProtectedRoute>
    ),
  },
  { path: "/setup", element: <Navigate to="/setup/settings" replace /> },
  {
    path: "/setup/:step",
    element: (
      <ProtectedRoute>
        <SetupWizard />
      </ProtectedRoute>
    ),
  },
  { path: "*", element: <Navigate to="/dashboard" replace /> },
];

// Every route gets the same localized crash fallback — see `RouteErrorElement`
// above for why this can't be done once at a shared ancestor instead.
export const router = createBrowserRouter(
  routeConfig.map((route) => ({ ...route, errorElement: <RouteErrorElement /> })),
);
